import type { SubmitHandler } from 'react-hook-form';
import { useState } from 'react';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateIapPollResponseBody } from '~/lib/api/modules/IapPolls';
import type { IUseUpdateIapPollMutationValues } from '~/lib/api/modules/IapPolls/hooks/useUpdateIapPollMutation';
import type { IIapPollFormValues } from './IapPollForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useIapPollQuery } from '~/lib/api/modules/IapPolls/hooks/useIapPollQuery';
import { useUpdateIapPollMutation } from '~/lib/api/modules/IapPolls/hooks/useUpdateIapPollMutation';
import { IapPollForm } from './IapPollForm';

export type IUpdateIapPollFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateIapPollResponseBody,
    inputValues: IUseUpdateIapPollMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  iapPollId: string;
};

export const UpdateIapPollForm: React.FC<IUpdateIapPollFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, iapPollId } = props;
  const iapPollQuery = useIapPollQuery({ appId, iapPollId });
  const updateIapPollMutation = useUpdateIapPollMutation();
  const [disabled, setDisabled] = useState(false);

  const iapPoll = iapPollQuery.data;

  const handleSubmit: SubmitHandler<IIapPollFormValues> = (values) => {
    if (updateIapPollMutation.isLoading) {
      return;
    }

    return updateIapPollMutation.mutateAsync(
      {
        appId,
        iapPollId,
        body: {
          active: !values.inactive,
          description: values.description,
          displayResults: values.displayResults,
          endDate: values.endDate!,
          options: values.options!.map((option) => ({
            optionId: option.optionId!,
            priceId: option.priceId ?? '',
            points: option.points!,
            maxVotesPerUserPerArticle: option.maxVotesPerUserPerArticle,
          })),
          startDate: values.startDate!,
          title: values.title!,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock draft={disabled} sx={sx} draftOutlineSize="lg">
      <ContentBlock.Suspense
        loading={iapPollQuery.isLoading}
        error={iapPollQuery.error}
        first
      >
        {iapPoll && (
          <IapPollForm
            mode="edition"
            cancelUrl={cancelUrl}
            defaultValues={{
              title: iapPoll.title,
              description: iapPoll.description,
              options: iapPoll.options?.map((option) => ({
                optionId: option.optionId,
                priceId: option.priceId ?? '',
                points: option.points,
                maxVotesPerUserPerArticle:
                  option.maxVotesPerUserPerArticle ?? 0,
              })),
              startDate: iapPoll.startDate,
              endDate: iapPoll.endDate,
              displayResults: iapPoll.displayResults,
              inactive: !iapPoll.active,
            }}
            onSubmit={handleSubmit}
            onDisabledStatusChange={setDisabled}
            first
          />
        )}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
